import { useTheme } from '@material-ui/core/styles';
import { isToday, isYesterday } from 'date-fns';
import { InputAdornment } from '@material-ui/core';
import { Content, Header, Page } from '@backstage/core-components';
import {
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubIconButton,
  DevHubPaper,
  DevHubTextField,
  DevHubTheme,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useEffect, useRef } from 'react';
import { useAgentChat } from './useAgentChat';
import { MarkdownMessage } from './MarkdownMessage';
import { CAPABILITY_CARDS, MAX_INPUT, TOOL_DISPLAY } from './constants';
import { Session } from './types';

/**
 * Nova chat page component. Renders the chat interface and handles user input.
 */
const AgentChatPage = () => {
  const theme = useTheme<DevHubTheme>();
  const blueGreen = theme.palette.gradients.blueGreen;
  const redPink = theme.palette.gradients.redPink;

  const {
    sessions,
    activeSessionId,
    stoppedMessages,
    currentMessages,
    input,
    setInput,
    loading,
    streamingContent,
    toolStatus,
    thinkingWord,
    error,
    githubConnected,
    copied,
    bottomRef,
    abortControllerRef,
    createSession,
    switchSession,
    deleteSession,
    sendMessage,
    handleKeyDown,
    handleCopy,
    handleFeedback,
    connectGitHub,
  } = useAgentChat();

  const showCounter = input.length > 1500;
  const counterColor = input.length > 1900 ? 'error' : 'textSecondary';

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
  }, [activeSessionId]);

  const todaySessions = sessions.filter(s => isToday(new Date(s.createdAt)));
  const yesterdaySessions = sessions.filter(s =>
    isYesterday(new Date(s.createdAt)),
  );
  const olderSessions = sessions.filter(
    s => !isToday(new Date(s.createdAt)) && !isYesterday(new Date(s.createdAt)),
  );

  const renderSessionGroup = (label: string, group: Session[]) => {
    if (group.length === 0) return null;
    return (
      <>
        <DevHubTypography
          variant="caption"
          color="textSecondary"
          style={{ padding: '8px 12px 4px', display: 'block' }}
        >
          {label}
        </DevHubTypography>
        {group.map(session => (
          <DevHubBox
            key={session.id}
            display="flex"
            alignItems="center"
            px={1.5}
            py={0.75}
            borderRadius={1}
            onClick={() => switchSession(session.id)}
            bgcolor={
              session.id === activeSessionId ? 'action.selected' : 'transparent'
            }
            style={{ cursor: 'pointer' }}
          >
            <DevHubTypography
              variant="body2"
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {session.title || 'New conversation'}
            </DevHubTypography>
            <DevHubTooltip title="Delete">
              <DevHubIconButton
                size="small"
                onClick={e => deleteSession(session.id, e)}
              >
                <DevHubDynamicIcon iconName="Delete" fontSize="small" />
              </DevHubIconButton>
            </DevHubTooltip>
          </DevHubBox>
        ))}
      </>
    );
  };

  return (
    <Page themeId="tool">
      <Header title="Nova" subtitle="Your DevHub AI Assistant" />
      <Content>
        <DevHubBox
          display="flex"
          flexDirection="row"
          height="calc(100vh - 150px)"
        >
          {/* Sidebar */}
          <DevHubBox
            width={220}
            flexShrink={0}
            display="flex"
            flexDirection="column"
            borderRight="1px solid"
            borderColor="divider"
            pr={1}
          >
            <DevHubButton
              variant="contained"
              buttonColor="blueGreen"
              textColor="white"
              size="small"
              onClick={() => createSession()}
            >
              <DevHubDynamicIcon
                iconName="Add"
                fontSize="small"
                style={{ marginRight: 4 }}
              />
              New Chat
            </DevHubButton>
            <DevHubBox flex={1} overflow="auto">
              {renderSessionGroup('Today', todaySessions)}
              {renderSessionGroup('Yesterday', yesterdaySessions)}
              {renderSessionGroup('Older', olderSessions)}
            </DevHubBox>
          </DevHubBox>

          {/* Chat area */}
          <DevHubBox
            flex={1}
            display="flex"
            flexDirection="column"
            pl={2}
            style={{ minWidth: 0, overflow: 'hidden' }}
          >
            {/* Messages */}
            <div
              ref={messagesContainerRef}
              style={{
                flex: 1,
                overflow: 'auto',
                marginBottom: 8,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {currentMessages.length === 0 && (
                <DevHubBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mt={4}
                  mb={2}
                  style={{ gap: 24 }}
                >
                  {/* Greeting */}
                  <DevHubBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    style={{ gap: 6 }}
                  >
                    <DevHubTypography variant="h5" align="center">
                      👋 Hi, I'm{' '}
                      <DevHubTypography
                        variant="h5"
                        fontColor="redPink"
                        style={{ display: 'inline' }}
                      >
                        Nova
                      </DevHubTypography>
                    </DevHubTypography>
                    <DevHubTypography
                      variant="body2"
                      color="textSecondary"
                      align="center"
                    >
                      Your DevHub AI assistant — here's some of what I can help
                      with:
                    </DevHubTypography>
                  </DevHubBox>

                  {/* Capability cards */}
                  <DevHubBox
                    display="grid"
                    style={{
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(220px, 1fr))',
                      columnGap: 8,
                      rowGap: 8,
                      width: '100%',
                      maxWidth: 860,
                    }}
                  >
                    {CAPABILITY_CARDS.map(card => (
                      <DevHubPaper
                        key={card.title}
                        slim
                        style={{ padding: '10px 12px', borderRadius: 8 }}
                      >
                        <DevHubBox
                          display="flex"
                          alignItems="center"
                          mb={0.5}
                          style={{ gap: 6 }}
                        >
                          <DevHubDynamicIcon
                            iconName={card.icon as any}
                            fontSize="small"
                            color="primary"
                          />
                          <DevHubTypography variant="subtitle2">
                            {card.title}
                          </DevHubTypography>
                        </DevHubBox>
                        <DevHubTypography
                          variant="caption"
                          color="textSecondary"
                          style={{
                            display: 'block',
                            marginBottom: card.examples.length ? 8 : 0,
                          }}
                        >
                          {card.description
                            .split(/(\*\*[^*]+\*\*)/)
                            .map((part, idx) =>
                              part.startsWith('**') && part.endsWith('**') ? (
                                <strong key={idx}>{part.slice(2, -2)}</strong>
                              ) : (
                                part
                              ),
                            )}
                        </DevHubTypography>
                        {card.examples.map(ex => (
                          <DevHubBox
                            key={ex}
                            px={1}
                            py={0.25}
                            mb={0.5}
                            borderRadius={1}
                            bgcolor="action.hover"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setInput(ex)}
                          >
                            <DevHubTypography
                              variant="caption"
                              color="textSecondary"
                            >
                              "{ex}"
                            </DevHubTypography>
                          </DevHubBox>
                        ))}
                      </DevHubPaper>
                    ))}
                  </DevHubBox>
                </DevHubBox>
              )}

              <DevHubBox
                display="flex"
                flexDirection="column"
                width="100%"
                style={{ minWidth: 0 }}
              >
                {currentMessages.map((msg, i) => {
                  const isStopped =
                    !!stoppedMessages[`${activeSessionId}:${i}`];
                  return (
                    <DevHubBox
                      key={i}
                      display="flex"
                      justifyContent={
                        msg.role === 'user' ? 'flex-end' : 'flex-start'
                      }
                      mb={1}
                      mr="40px"
                      style={{ minWidth: 0, overflow: 'hidden' }}
                    >
                      <DevHubBox
                        bgcolor={
                          msg.role === 'user' ? 'primary.main' : 'action.hover'
                        }
                        color={
                          msg.role === 'user'
                            ? 'primary.contrastText'
                            : 'text.primary'
                        }
                        px={2}
                        py={1}
                        borderRadius={2}
                        maxWidth="85%"
                        style={
                          msg.role === 'assistant'
                            ? {
                                borderTop: '2px solid transparent',
                                borderImage: `${isStopped ? redPink : blueGreen} 1`,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                minWidth: 0,
                                overflow: 'hidden',
                              }
                            : {
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                minWidth: 0,
                                overflow: 'hidden',
                              }
                        }
                      >
                        {msg.role === 'user' ? (
                          <DevHubTypography
                            variant="body2"
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {msg.content}
                          </DevHubTypography>
                        ) : (
                          <>
                            <MarkdownMessage content={msg.content} />
                            {isStopped && (
                              <DevHubTypography
                                variant="caption"
                                color="textSecondary"
                                style={{
                                  display: 'block',
                                  marginTop: 4,
                                  fontStyle: 'italic',
                                }}
                              >
                                Response was stopped
                              </DevHubTypography>
                            )}
                            {/* Action row */}
                            <DevHubBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mt={0.5}
                            >
                              <DevHubBox
                                display="flex"
                                alignItems="center"
                                style={{ gap: 2 }}
                              >
                                <DevHubTooltip
                                  title={
                                    copied[`${activeSessionId}:${i}`]
                                      ? 'Copied!'
                                      : 'Copy response'
                                  }
                                >
                                  <DevHubIconButton
                                    size="small"
                                    onClick={() =>
                                      handleCopy(
                                        `${activeSessionId}:${i}`,
                                        msg.content,
                                      )
                                    }
                                  >
                                    <DevHubDynamicIcon
                                      iconName={
                                        copied[`${activeSessionId}:${i}`]
                                          ? 'CheckCircle'
                                          : 'RateReview'
                                      }
                                      fontSize="small"
                                    />
                                  </DevHubIconButton>
                                </DevHubTooltip>
                                <DevHubTooltip title="Helpful">
                                  <DevHubIconButton
                                    size="small"
                                    onClick={() =>
                                      handleFeedback(
                                        `${activeSessionId}`,
                                        i,
                                        'up',
                                      )
                                    }
                                  >
                                    <DevHubDynamicIcon
                                      iconName="ThumbUp"
                                      fontSize="small"
                                      color={
                                        msg.feedback === 'up'
                                          ? 'success'
                                          : undefined
                                      }
                                    />
                                  </DevHubIconButton>
                                </DevHubTooltip>
                                <DevHubTooltip title="Not helpful">
                                  <DevHubIconButton
                                    size="small"
                                    onClick={() =>
                                      handleFeedback(
                                        `${activeSessionId}`,
                                        i,
                                        'down',
                                      )
                                    }
                                  >
                                    <DevHubDynamicIcon
                                      iconName="ThumbDown"
                                      fontSize="small"
                                      color={
                                        msg.feedback === 'down'
                                          ? 'error'
                                          : undefined
                                      }
                                    />
                                  </DevHubIconButton>
                                </DevHubTooltip>
                              </DevHubBox>
                              {msg.tools && msg.tools.length > 0 && (
                                <DevHubBox
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: 4 }}
                                >
                                  <DevHubTypography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ marginRight: 2 }}
                                  >
                                    Sources:
                                  </DevHubTypography>
                                  {Array.from(
                                    new Set(
                                      msg.tools.map(
                                        t => TOOL_DISPLAY[t]?.label ?? t,
                                      ),
                                    ),
                                  ).map(label => {
                                    const toolName = msg.tools!.find(
                                      t =>
                                        (TOOL_DISPLAY[t]?.label ?? t) === label,
                                    )!;
                                    const display = TOOL_DISPLAY[toolName];
                                    return (
                                      <DevHubChip
                                        key={label}
                                        label={label}
                                        chipSize="small"
                                        chipColor="white"
                                        noMargins
                                        icon={
                                          display ? (
                                            <DevHubDynamicIcon
                                              iconName={display.icon as any}
                                              fontSize="small"
                                            />
                                          ) : undefined
                                        }
                                      />
                                    );
                                  })}
                                </DevHubBox>
                              )}
                            </DevHubBox>
                          </>
                        )}
                      </DevHubBox>
                    </DevHubBox>
                  );
                })}

                {loading && !streamingContent && !toolStatus && (
                  <DevHubBox display="flex" justifyContent="flex-start" mb={1}>
                    <DevHubBox px={2} py={1}>
                      <DevHubTypography variant="body2" color="textSecondary">
                        {thinkingWord}...
                      </DevHubTypography>
                    </DevHubBox>
                  </DevHubBox>
                )}
                {loading && toolStatus && !streamingContent && (
                  <DevHubBox display="flex" justifyContent="flex-start" mb={1}>
                    <DevHubBox px={2} py={1}>
                      <DevHubTypography variant="body2" color="textSecondary">
                        {toolStatus}
                      </DevHubTypography>
                    </DevHubBox>
                  </DevHubBox>
                )}
                {loading && streamingContent && (
                  <DevHubBox
                    display="flex"
                    justifyContent="flex-start"
                    mb={1}
                    mr="40px"
                    style={{ minWidth: 0, overflow: 'hidden' }}
                  >
                    <DevHubBox
                      bgcolor="action.hover"
                      color="text.primary"
                      px={2}
                      py={1}
                      borderRadius={2}
                      maxWidth="85%"
                      style={{
                        borderTop: '2px solid transparent',
                        borderImage: `${redPink} 1`,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <MarkdownMessage content={streamingContent} />
                    </DevHubBox>
                  </DevHubBox>
                )}
                {error && (
                  <DevHubBox display="flex" justifyContent="flex-start" mb={1}>
                    <DevHubBox px={2} py={1}>
                      <DevHubTypography variant="body2" color="error">
                        {error}
                      </DevHubTypography>
                    </DevHubBox>
                  </DevHubBox>
                )}
              </DevHubBox>
              <div ref={bottomRef} />
            </div>

            {/* GitHub connect banner */}
            {githubConnected === false && (
              <DevHubBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1}
                mb={1}
                borderRadius={1}
                bgcolor="action.hover"
              >
                <DevHubTypography variant="body2">
                  Connect GitHub to enable additional features
                </DevHubTypography>
                <DevHubButton
                  size="small"
                  variant="outlined"
                  onClick={connectGitHub}
                >
                  Connect GitHub
                </DevHubButton>
              </DevHubBox>
            )}

            {/* Input area */}
            <DevHubBox maxWidth="calc(100% - 40px)" ml={-3}>
              <DevHubTextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask about a service, API, team..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                variant="outlined"
                size="medium"
                inputProps={{ maxLength: MAX_INPUT }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {loading ? (
                        <DevHubButton
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => abortControllerRef.current?.abort()}
                        >
                          Stop
                        </DevHubButton>
                      ) : (
                        <DevHubButton
                          size="small"
                          variant="contained"
                          buttonColor="purpleBlue"
                          onClick={sendMessage}
                          disabled={!input.trim()}
                        >
                          <DevHubTypography fontColor="white">
                            Send
                          </DevHubTypography>
                        </DevHubButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {showCounter && (
                <DevHubBox
                  display="flex"
                  justifyContent="flex-end"
                  mt={0.5}
                  px={0.5}
                >
                  <DevHubTypography variant="caption" color={counterColor}>
                    {input.length} / {MAX_INPUT}
                  </DevHubTypography>
                </DevHubBox>
              )}
            </DevHubBox>
          </DevHubBox>

          {/* Right margin */}
          <DevHubBox width={16} flexShrink={0} />
        </DevHubBox>
      </Content>
    </Page>
  );
};

export default AgentChatPage;
